import React, { useMemo, useState } from "react";
import { CalendarOff, X } from "lucide-react";
import { espaceApi, messageErreur } from "./api";

const JOURS = ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"];
const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const lisible = (j) => {
  const d = new Date(`${j}T12:00:00`);
  return `${JOURS[d.getDay()]} ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}`;
};

/** La prochaine séance d'un cours (aujourd'hui compris). */
const prochaineSeance = (cours) => {
  const aujourdhui = new Date();
  const cible = JOURS.indexOf(String(cours?.jour || "").trim().toLowerCase());
  if (cible < 0) return iso(aujourdhui);
  const d = new Date(aujourdhui);
  let ecart = (cible - d.getDay() + 7) % 7;
  // Le cours d'aujourd'hui a déjà commencé : on propose la semaine prochaine (l'école
  // refuse une absence annoncée après le début du cours).
  const [h, m] = String(cours?.heure || "").split(":").map(Number);
  if (ecart === 0 && Number.isFinite(h) && aujourdhui.getHours() * 60 + aujourdhui.getMinutes() >= h * 60 + (m || 0)) ecart = 7;
  d.setDate(d.getDate() + ecart);
  return iso(d);
};

/* « Léa ne viendra pas mercredi » : en deux taps, jusqu'à l'heure du cours. L'école et le
   prof sont prévenus, et Léa porte la pastille « Absent » sur la liste de CE cours-là. */
export const FormulaireAbsence = ({ enfant, onChange, onFini }) => {
  const [coursId, setCoursId] = useState(enfant.cours[0]?.cours_id || "");
  const cours = useMemo(() => enfant.cours.find((c) => c.cours_id === coursId), [enfant, coursId]);
  const [du, setDu] = useState(() => prochaineSeance(enfant.cours[0]));
  const [periode, setPeriode] = useState(false);
  const [au, setAu] = useState("");
  const [motif, setMotif] = useState("");
  const [erreur, setErreur] = useState("");
  const [envoi, setEnvoi] = useState(false);

  const choisirCours = (id) => {
    setCoursId(id);
    const c = enfant.cours.find((x) => x.cours_id === id);
    if (c) setDu(prochaineSeance(c));
  };

  const annoncer = async () => {
    setErreur(""); setEnvoi(true);
    try {
      const { data } = await espaceApi.post("/absence", {
        eleve_id: enfant.id, cours_id: coursId, du, au: periode ? au : "", motif,
      });
      onFini?.(data.message);
      onChange?.();
    } catch (e) {
      setErreur(messageErreur(e));
    }
    setEnvoi(false);
  };

  return (
    <div className="space-y-3 text-sm" data-testid="espace-absence-formulaire">
      <p className="text-[15px] font-semibold">Absence de {enfant.prenom}</p>
      <label className="block">
        <span className="text-xs text-[var(--muted)]">Quel cours ?</span>
        <select value={coursId} onChange={(e) => choisirCours(e.target.value)}
          className="input-field mt-1 w-full" data-testid="espace-absence-cours">
          {enfant.cours.map((c) => (
            <option key={c.id} value={c.cours_id}>{c.nom} ({c.jour} {c.heure})</option>
          ))}
          {enfant.cours.length > 1 && <option value="">Tous ses cours</option>}
        </select>
      </label>
      <label className="block">
        <span className="text-xs text-[var(--muted)]">
          {periode ? "Du" : "Le"}{cours ? ` (le cours a lieu le ${cours.jour.toLowerCase()})` : ""}
        </span>
        <input type="date" value={du} min={iso(new Date())} onChange={(e) => setDu(e.target.value)}
          className="input-field mt-1 w-full" data-testid="espace-absence-du" />
      </label>
      <label className="flex items-center gap-2 text-[13px] min-h-[36px]">
        <input type="checkbox" checked={periode} onChange={(e) => setPeriode(e.target.checked)}
          className="w-5 h-5" data-testid="espace-absence-periode" />
        Plusieurs semaines (vacances, blessure…)
      </label>
      {periode && (
        <label className="block">
          <span className="text-xs text-[var(--muted)]">Jusqu'au (inclus)</span>
          <input type="date" value={au} min={du} onChange={(e) => setAu(e.target.value)}
            className="input-field mt-1 w-full" data-testid="espace-absence-au" />
        </label>
      )}
      <label className="block">
        <span className="text-xs text-[var(--muted)]">Un mot pour l'école (facultatif)</span>
        <input value={motif} onChange={(e) => setMotif(e.target.value)} maxLength={300}
          placeholder="Malade, vacances…" className="input-field mt-1 w-full" data-testid="espace-absence-motif" />
      </label>
      {erreur && <p className="text-xs" style={{ color: "#B91C1C" }}>{erreur}</p>}
      <div className="flex gap-2 pt-1">
        <button onClick={annoncer} disabled={envoi || !du || (periode && !au)}
          className="btn-primary text-sm flex-1 justify-center" data-testid="espace-absence-envoyer">
          {envoi ? "Envoi…" : "Prévenir l'école"}
        </button>
        <button onClick={() => onFini?.("")} className="btn-ghost text-sm">
          <X size={14} /> Annuler
        </button>
      </div>
    </div>
  );
};

/* Les absences déjà annoncées, avec de quoi les retirer (l'enfant vient finalement). */
export const AbsencesAnnoncees = ({ enfant, onChange }) => {
  const [erreur, setErreur] = useState("");
  if (!(enfant.absences || []).length) return null;
  const retirer = async (a) => {
    setErreur("");
    try {
      await espaceApi.delete(`/absence/${encodeURIComponent(a.id)}`);
      onChange?.();
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };
  return (
    <div className="rounded-[22px] px-4 py-3 space-y-2" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }}
      data-testid={`espace-absence-${enfant.id}`}>
      <div className="text-[13px] font-semibold flex items-center gap-1.5" style={{ color: "#92400E" }}>
        <CalendarOff size={14} /> Absences annoncées
      </div>
      {enfant.absences.map((a) => (
        <div key={a.id} className="flex items-center justify-between gap-3 text-[13.5px]"
          data-testid="espace-absence-annoncee">
          <span className="min-w-0">
            <strong>{a.du === a.au ? lisible(a.du) : `du ${lisible(a.du)} au ${lisible(a.au)}`}</strong>
            <span className="text-[var(--muted)]"> · {a.cours_nom}</span>
          </span>
          <button onClick={() => retirer(a)} className="text-[13px] font-medium shrink-0 min-h-[36px]"
            style={{ color: "var(--primary)" }} data-testid="espace-absence-retirer">
            Retirer
          </button>
        </div>
      ))}
      {erreur && <p className="text-xs" style={{ color: "#B91C1C" }}>{erreur}</p>}
    </div>
  );
};
